import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Banner, Field, FormShell } from "@/components/auth/FormShell";
import {
  PasswordRule,
  PasswordStrengthBar,
  scorePassword,
} from "@/components/auth/PasswordStrength";
import formStyles from "@/components/auth/AuthForm.module.css";
import { confirmPasswordReset, type AuthError } from "@/lib/auth";

export function ResetConfirm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = (params.get("email") ?? "").trim();

  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState<AuthError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { meetsLength, meetsCase, meetsNumber } = scorePassword(pw);
  const matches = pw.length > 0 && pw === pw2;
  const canSubmit = !!email && code.length === 6 && meetsLength && meetsCase && meetsNumber && matches;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(email, code, pw);
      navigate("/signin?reset=1", { replace: true });
    } catch (err) {
      setError(err as AuthError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout view="reset-confirm">
      <FormShell
        title="Set a new password"
        subtitle={
          email ? (
            <>
              For <span className={formStyles.emailEmphasis}>{email}</span>. We’ll sign
              out your other devices automatically.
            </>
          ) : (
            "We need the code we sent to your email, and a new password to set."
          )
        }
      >
        <form onSubmit={onSubmit} noValidate className={formStyles.stack}>
          {error && (
            <Banner variant="danger" icon={<Icon name="info" size={14} />}>
              {error.message}
            </Banner>
          )}
          <Field label="6-digit code" htmlFor="reset-code">
            <Input
              id="reset-code"
              icon="lock"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="From the email"
              required
            />
          </Field>
          <Field
            label="New password"
            hint="At least 12 characters, upper + lower case, and a number."
            htmlFor="reset-pw"
          >
            <Input
              id="reset-pw"
              icon="lock"
              type="password"
              autoComplete="new-password"
              suffix="show"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 12 characters"
              required
              minLength={12}
            />
            {pw.length > 0 && <PasswordStrengthBar password={pw} />}
          </Field>
          <Field label="Confirm new password" htmlFor="reset-pw2">
            <Input
              id="reset-pw2"
              icon="lock"
              type="password"
              autoComplete="new-password"
              suffix="show"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Type it again"
              required
              invalid={pw2.length > 0 && !matches}
            />
          </Field>
          <div className={formStyles.tightStack}>
            <PasswordRule met={meetsLength}>At least 12 characters</PasswordRule>
            <PasswordRule met={meetsCase}>Mix of upper and lower case</PasswordRule>
            <PasswordRule met={meetsNumber}>Contains at least one number</PasswordRule>
            <PasswordRule met={matches}>Both password fields match</PasswordRule>
          </div>
          <Btn
            type="submit"
            variant="primary"
            full
            size="lg"
            loading={submitting}
            disabled={!canSubmit}
            iconRight={<Icon name="check" size={14} />}
          >
            Update password
          </Btn>
          <div className={formStyles.backRow}>
            <Link to="/signin" className={formStyles.backLink}>
              <Icon name="arrow-left" size={12} />
              Back to sign in
            </Link>
          </div>
        </form>
      </FormShell>
    </AuthLayout>
  );
}
